"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState, useTransition } from "react";
import FocusLock from "react-focus-lock";

import CurrencyButton from "@/components/HeaderSettings/CurrencyButton";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { NavigationContext } from "@/context/NavigationContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import { DL_KEYS } from "@/dataLayer/keys";
import { HeaderSettingsProps } from "@/types";

const HeaderSettings: FC<HeaderSettingsProps> = ({ settings }) => {
  const t = useTranslations();
  const [trigger, setTrigger] = useState<HTMLElement>();
  const { settingsVisible, setSettingsVisible, setTransitionComplete } = useContext(NavigationContext);
  const { currency, measurement, changeCurrencyAndMeasurement } = useContext(UserContext);
  const [checkedCurrency, setCheckedCurrency] = useState<string>(currency);
  const [checkedMeasure, setCheckedMeasure] = useState<string>(measurement);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currency && measurement) {
      setCheckedCurrency(currency);
      setCheckedMeasure(measurement);
    }
  }, [currency, measurement]);

  const keyboardHandler = useCallback(
    (event: KeyboardEvent) => {
      if (settingsVisible && event.key.toLowerCase() === "escape") {
        setSettingsVisible(false);
      }
    },
    [setSettingsVisible, settingsVisible],
  );

  useEffect(() => {
    if (document) {
      document.addEventListener("keydown", keyboardHandler);
      return () => {
        document.removeEventListener("keydown", keyboardHandler);
      };
    }
  }, [keyboardHandler]);

  //TODO "useClickOutside"
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsVisible && panelRef?.current && !panelRef?.current?.contains(event.target as HTMLElement)) {
        console.log("clickoutside");
        setSettingsVisible(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [panelRef, setSettingsVisible, settingsVisible]);

  const router = useRouter();

  const handleConfirmAction = () => {
    // setCurrency(checkedCurrency);
    // setMeasurement(checkedMeasure);
    changeCurrencyAndMeasurement(checkedCurrency, checkedMeasure);
    setSettingsVisible(false);
    // ctaClickTracking(t("button.label.apply"), "HeaderSettings", "HeaderSettings");
    ctaClickTracking(DL_KEYS?.apply, "HeaderSettings", "HeaderSettings");
    router.refresh();
  };

  const overlayVariant = {
    open: { opacity: 1, display: "block", transition: { duration: 0.3, when: "beforeChildren", staggerChildren: 0.5, childrenDelay: 0.3 } },
    close: {
      opacity: 0,
      transition: { duration: 0.3, when: "afterChildren", staggerChildren: 0.5, childrenDelay: 0.3 },
      transitionEnd: { display: "none" },
    },
  };

  const popupVariant = {
    open: { y: "0%", transition: { ease: "easeOut", duration: 0.5 } },
    close: { y: "100%" },
  };

  return (
    <motion.div
      id="modal-settings"
      variants={overlayVariant}
      initial="close"
      animate={settingsVisible ? "open" : "close"}
      className={`fixed left-0 top-0 md:top-[28px] w-screen h-dvh md:h-[calc(100dvh-28px)] bg-[rgba(0,0,0,0.3)] z-[51]`}
      onAnimationComplete={() => {
        setTransitionComplete(true);
      }}
      onAnimationStart={() => {
        setTransitionComplete(false);
      }}
    >
      <FocusLock
        autoFocus={true}
        returnFocus={(el) => {
          if (!trigger) {
            setTrigger(el as HTMLElement);
          } else {
            trigger.focus();
            setTrigger(undefined);
          }
          return true;
        }}
        disabled={!settingsVisible}
        className="w-full h-full"
      >
        <div className="w-full h-full flex items-end md:items-center overflow-hidden md:overflow-auto">
          <div className="w-full">
            <div className="mx-auto header-modal w-full md:w-[440px]">
              <motion.div
                initial={"close"}
                ref={panelRef}
                variants={popupVariant}
                animate={settingsVisible ? "open" : "close"}
                className="bg-white px-m py-s rounded-t-[30px] md:rounded-[30px] relative"
              >
                <div className="bg-gray-2 mx-auto w-[36px] h-[5px] rounded md:hidden"></div>
                <p className="hidden md:flex md:items-center p-m font-primary text-m uppercase font-medium">
                  {t("header.labels.settings")}

                  <Button
                    type="button"
                    className="modal__close stroke-black ml-auto"
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.TERTIARY}
                    theme={ButtonTheme.BLACK}
                    onClick={() => {
                      setSettingsVisible(false);
                      setCheckedCurrency(currency);
                      setCheckedMeasure(measurement);
                      // ctaClickTracking(t("button.label.close"), "HeaderSettings", "HeaderSettings");
                      ctaClickTracking(DL_KEYS?.close, "HeaderSettings", "HeaderSettings");
                    }}
                  >
                    <>
                      <Icon name="close" size="24" title="close" />
                      <span className="sr-only">{t("button.label.close")}</span>
                    </>
                  </Button>
                </p>
                <p className="font-primary font-medium text-s uppercase mt-xl mb-l">{t("header.labels.currency")}</p>
                <form id="user-settings" name="user-settings">
                  <div className="grid grid-cols-2 grid-rows-auto gap-s pb-l">
                    {settings?.currencies?.map((item) => {
                      return (
                        <CurrencyButton
                          clickHandler={() => {
                            setCheckedCurrency(item);
                          }}
                          selected={item === checkedCurrency}
                          key={`header-currency-${item}`}
                          label={t(`header.currency.${item}`)}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between items-center py-l border-t border-t-gray-10">
                    <p className="font-primary font-medium text-s uppercase">{t("header.labels.measurement")}</p>
                    <div className="flex bg-gray-10 rounded-3xl p-xxs">
                      {settings?.measurements?.map((item) => {
                        return (
                          <CurrencyButton
                            clickHandler={() => {
                              setCheckedMeasure(item);
                            }}
                            type="switch"
                            selected={item === checkedMeasure}
                            key={`header-measure-${item}`}
                            label={t(`header.measurement.${item}`)}
                          />
                        );
                      })}
                    </div>
                  </div>
                </form>
                <div>
                  <Button
                    type="button"
                    customClass="w-full"
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.BLACK}
                    onClick={handleConfirmAction}
                  >
                    {t("button.label.apply")}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FocusLock>
    </motion.div>
  );
};

export default HeaderSettings;
